/**
 * What the modifiers do to a mission before a single prop is placed (03 §5.1 step 9):
 * `storm` and `night-op` set the weather and the hour, `short-fuse` cuts the clock,
 * `high-alert` wakes the garrison. Also the wind, which follows the weather (02 §3.2),
 * and the mission clock (03 §1).
 */

import { cos, sin, TAU } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import type { MissionParams } from '../sim/types';
import type { ArchetypeSpec } from './archetypes/types';
import type { BiomeKit } from './biomes';
import { round2 } from './builder';
import { CLOCK, LIMITS, MAP, WIND } from './tuning';

/** The parameters the map is actually built for: weather and time of day reflect the modifiers. */
export function effectiveParams(params: MissionParams, kit: BiomeKit): MissionParams {
  const threat = Math.min(9, Math.max(1, Math.round(params.threat)));
  return {
    ...params,
    threat,
    modifiers: [...params.modifiers],
    intel: [...params.intel],
    weather: params.modifiers.includes('storm') ? kit.storm : params.weather,
    timeOfDay: params.modifiers.includes('night-op') ? 'night' : params.timeOfDay,
  };
}

export function windFor(rng: Pcg32, params: MissionParams): { x: number; z: number } {
  const [lo, hi] = WIND[params.weather];
  const speed = rng.range(lo, hi);
  const heading = rng.range(0, TAU);
  return { x: round2(sin(heading) * speed), z: round2(cos(heading) * speed) };
}

/** 600 s for the default strip, scaled by length, threat (02 §12), the archetype and `short-fuse`. */
export function clockFor(spec: ArchetypeSpec, params: MissionParams, sizeZ: number): number {
  const byLength = sizeZ / (MAP.lengthTiles * MAP.tile);
  const byThreat = params.threat <= 2 ? CLOCK.lowThreat : params.threat >= 7 ? CLOCK.highThreat : 1;
  const byFuse = params.modifiers.includes('short-fuse') ? CLOCK.shortFuse : 1;
  return Math.round((CLOCK.baseSeconds * byLength * byThreat * byFuse * spec.clockScale) / 5) * 5;
}

export function startAlertFor(spec: ArchetypeSpec, params: MissionParams): number {
  return Math.max(spec.startAlert, params.modifiers.includes('high-alert') ? 1 : 0);
}

/** 10-40 soldiers by threat (dormant ones included); a garrison on high alert may field a few more. */
export function spawnCapFor(params: MissionParams): number {
  const t = (params.threat - 1) / 8;
  const base = Math.round(LIMITS.spawnsAt1 + (LIMITS.spawnsAt9 - LIMITS.spawnsAt1) * t);
  return params.modifiers.includes('high-alert') ? Math.min(LIMITS.spawnsAt9, Math.round(base * 1.25)) : base;
}
